/** Exhaustive, ordered continuity recipes. Shape ranks safe in-band plans; it never admits one. */
import type { Cadence, CompositionProof, Recording, ReviewedSpanVariant, SessionPlan, Timed } from '../types.ts';
import {
  comingOutIsland, cueReviewFor, cueTrimFor, effectiveGaps, InvalidEditError, normalizeEdits,
  planDiagnostics, protectedTransitions, retainedCueConflicts, speechSegments,
  type PlanOptions,
} from './legacy-plan.ts';
import { COMPOSITION_POLICY_VERSION, compositionPolicy } from './composition-policy.ts';
import { isWithheld } from './withheld.ts';

export type CompositionFailure = 'withheld' | 'invalid-review' | 'cue-conflict' | 'duration' | 'speech-band' | 'no-safe-ending';
export interface TargetInterval { minSec: number; maxSec: number; minInclusive?: boolean; maxInclusive?: boolean }
export type CompositionResult =
  | { status: 'conforming'; plan: SessionPlan; proof: CompositionProof; feasibleTargets: TargetInterval[]; durationPossible: true }
  | { status: 'unavailable'; reason: CompositionFailure; plan: null; detail: string;
      feasibleTargets: TargetInterval[]; durationPossible: boolean };
type Bounds = { island: number; srcStart: number; srcEnd: number };
type Group = { islands: number[]; mandatory: boolean; role: 'opening'|'middle'|'closing' };
type Passage = { group: Group; bounds: Bounds[]; variant?: {id: string; decisionId: string}; spokenSec: number; trimmedSec: number };
type Gaps = ReturnType<typeof effectiveGaps>;
type Recipe = { passages: Passage[]; kept: number[]; gaps: Gaps; internal: boolean[];
  spokenSec: number; floor: number; ceiling: number; trims: ReturnType<typeof cueTrimFor> };

// Bind caches to the complete planning inputs, including edits made in place by QA.
// There are only seven preset lengths; deadline requests use a bounded LRU below.
export function compositionSourceKey(rec: Recording): string {
  return JSON.stringify([rec.id,rec.nativeSec,rec.speech.map(s=>[s.srcStart,s.srcEnd,s.role]),
    rec.gaps.map(g=>[g.afterIsland,g.nativeSec,g.minSec,g.maxSec]),rec.durationCues,
    rec.phaseVariants,rec.spanVariants,rec.wordlessIslands,rec.linkDecisions]);
}
const caches = new WeakMap<Recording, {key:string; cueRecipes: Map<string,Recipe[]>; intervals: Map<Cadence,TargetInterval[]>; results: Map<string,CompositionResult>}>();
function cacheFor(rec: Recording) {
  const key = compositionSourceKey(rec);
  let cache = caches.get(rec);
  if (!cache || cache.key !== key) {
    cache = {key,cueRecipes:new Map(),intervals:new Map(),results:new Map()}; caches.set(rec,cache);
  }
  return cache;
}
function fail(reason: CompositionFailure, detail: string, feasibleTargets: TargetInterval[] = [], durationPossible = false): CompositionResult {
  return {status:'unavailable',reason,detail,plan:null,feasibleTargets,durationPossible};
}
export function continuityGroups(rec: Recording): Group[] {
  const linked = protectedTransitions(rec), pin = comingOutIsland(rec);
  const groups: Group[] = [];
  let start = 0;
  for (let end = 0; end < rec.speech.length; end++) {
    if (linked.has(end)) continue;
    const islands = Array.from({length:end-start+1},(_,i)=>start+i);
    groups.push({islands,mandatory: start === 0 || end === rec.speech.length-1 || (pin !== null && islands.includes(pin)),
      role:start===0?'opening':end===rec.speech.length-1?'closing':'middle'});
    start = end + 1;
  }
  return groups;
}
function validateSource(rec: Recording) {
  let end = -Infinity;
  for (const s of rec.speech) {
    if (![s.srcStart,s.srcEnd].every(Number.isFinite) || s.srcStart < end || s.srcStart < 0 || s.srcEnd <= s.srcStart) {
      throw new InvalidEditError(rec.id,'invalid or unordered speech islands');
    }
    end = s.srcEnd;
  }
  if (!rec.speech.length) throw new InvalidEditError(rec.id,'recording contains no speech');
  for (const g of rec.gaps) {
    if (![g.nativeSec,g.minSec,g.maxSec].every(Number.isFinite) || g.minSec < 0 || g.maxSec < g.minSec || g.nativeSec < 0) {
      throw new InvalidEditError(rec.id,'invalid gap bounds');
    }
  }
  const wordless = rec.wordlessIslands ?? [];
  if (new Set(wordless).size !== wordless.length || wordless.some(i=>!Number.isInteger(i)||!rec.speech[i])) {
    throw new InvalidEditError(rec.id,'invalid wordless island indexes');
  }
}
function recipes(rec: Recording, target: number): Recipe[] {
  const cache = cacheFor(rec);
  validateSource(rec);
  const groups = continuityGroups(rec), linked = protectedTransitions(rec);
  const originalGroups = rec.linkDecisions?.length ? continuityGroups({...rec,linkDecisions:undefined}) : groups;
  const trims = normalizeEdits(rec,cueTrimFor(rec,target));
  const conflicts = retainedCueConflicts(rec,target);
  const cueKey = JSON.stringify([trims,conflicts]);
  const cached = cache.cueRecipes.get(cueKey);
  if (cached) return cached;
  const wordless = new Set(rec.wordlessIslands ?? []), pin = comingOutIsland(rec);
  const variants = new Map<Group, Array<{variant:ReviewedSpanVariant; legacy:boolean}>>();
  const ids = new Set<string>();
  const addVariant = (v: ReviewedSpanVariant, legacy: boolean, island?: number) => {
    if (!v.id || ids.has(v.id) || v.review?.status !== 'approved' || !v.review.decisionId
        || (!legacy && !['ear','quorum:cuts-v1'].includes(v.review.by))
        || !['opening','middle','closing'].includes(v.role)) {
      throw new InvalidEditError(rec.id,`variant ${v.id} is not uniquely approved`);
    }
    ids.add(v.id);
    const first = rec.speech.findIndex(s=>v.srcStart >= s.srcStart && v.srcStart < s.srcEnd);
    const last = rec.speech.findIndex(s=>v.srcEnd > s.srcStart && v.srcEnd <= s.srcEnd);
    const group = groups.find(g=>g.islands.includes(first)&&g.islands.includes(last));
    const original = originalGroups.find(g=>g.islands.includes(first)&&g.islands.includes(last));
    // A previously approved span is indivisible. Splitting its group does not approve
    // its intersections, or a new phase role. Hold that alternative, not the recording.
    if (!legacy && original && originalGroups !== groups
        && (v.role !== 'opening' || original === originalGroups[0])
        && (v.role !== 'closing' || original === originalGroups.at(-1) || original.islands.includes(pin ?? -1))
        && (v.role !== 'middle' || (original !== originalGroups[0] && original !== originalGroups.at(-1))
          || (v.srcStart === rec.speech[original.islands[0]].srcStart && v.srcEnd === rec.speech[original.islands.at(-1)!].srcEnd))
        && Number.isFinite(v.srcStart) && Number.isFinite(v.srcEnd) && v.srcStart < v.srcEnd
        && (!group || (v.role === 'opening' && group !== groups[0])
          || (v.role === 'closing' && group !== groups.at(-1) && !group.islands.includes(pin ?? -1)))) return;
    if (!Number.isFinite(v.srcStart) || !Number.isFinite(v.srcEnd) || v.srcStart >= v.srcEnd || !group
        || (legacy && (first !== island || last !== island))
        || (v.role === 'opening' && group !== groups[0])
        || (v.role === 'closing' && group !== groups.at(-1) && !group.islands.includes(pin ?? -1))
        || (v.role === 'middle' && (group === groups[0] || group === groups.at(-1))
          && (v.srcStart !== rec.speech[group.islands[0]].srcStart || v.srcEnd !== rec.speech[group.islands.at(-1)!].srcEnd))) {
      throw new InvalidEditError(rec.id,`variant ${v.id} is outside its continuity group`);
    }
    // Legacy one-island reviews do not authorize removing linked context.
    if (legacy && ((v.role==='opening'&&linked.has(first)) || (v.role==='closing'&&linked.has(first-1)))) return;
    variants.set(group,[...(variants.get(group) ?? []),{variant:v,legacy}]);
  };
  // A cut boundary must never land inside a duration-cue sentence. Cue sentences state a
  // length ("stillness for 30 minutes"), and their transcript times are interpolated, so an
  // acoustically snapped boundary 1.1 s into one (9 December 2005, 80.13 s) cannot be shown
  // to keep the sentence whole. Such a span is unusable here, not repaired.
  const insideCue=(at:number)=>rec.durationCues.some(c=>at>c.atSec+1e-6&&at<c.endSec-1e-6);
  for (const v of rec.spanVariants ?? []) if(!insideCue(v.srcStart)&&!insideCue(v.srcEnd)) addVariant(v,false);
  for (const role of ['opening','closing'] as const) for (const v of rec.phaseVariants?.[role] ?? []) {
    if (v.boundaryBasis !== 'complete-thought') throw new InvalidEditError(rec.id,`variant ${v.id} is not a complete thought`);
    addVariant({...v,role,review:{...v.review,by:'ear'}},true,v.island);
  }
  const choices = groups.map(group => {
    if (group.islands.every(i=>wordless.has(i))) return [null];
    const options: Array<Passage|null> = group.mandatory ? [] : [null];
    for (const v of [undefined,...(variants.get(group) ?? []).map(x=>x.variant)]) {
      const bounds = group.islands.filter(i=>!wordless.has(i)).flatMap(island=>{
        const s=rec.speech[island], srcStart=Math.max(s.srcStart,v?.srcStart ?? s.srcStart), srcEnd=Math.min(s.srcEnd,v?.srcEnd ?? s.srcEnd);
        return srcEnd > srcStart ? [{island,srcStart,srcEnd}] : [];
      });
      if (!bounds.length) { if (!group.mandatory) continue; /* No speech can fulfil a boundary. */ continue; }
      // A shorter closing discharges only its own group's coming-out obligation.
      if (pin !== null && !wordless.has(pin) && group.islands.includes(pin)
          && !bounds.some(b=>b.island===pin && b.srcStart===rec.speech[pin].srcStart && b.srcEnd===rec.speech[pin].srcEnd)
          && v?.role !== 'closing') continue;
      if (group === groups[0] && !bounds.some(b=>b.island===0) && !wordless.has(0) && v?.role !== 'opening') continue;
      if (group === groups.at(-1) && !bounds.some(b=>b.island===rec.speech.length-1)
          && !wordless.has(rec.speech.length-1) && v?.role !== 'closing') continue;
      let spokenSec=0,trimmedSec=0,valid=true;
      for (const b of bounds) {
        const overlaps=(t: {island:number;fromSec:number;toSec:number})=>t.island===b.island&&t.toSec>b.srcStart&&t.fromSec<b.srcEnd;
        if (conflicts.some(overlaps)) {valid=false;break;}
        const mine=trims.filter(overlaps);
        // A span approval describes one contiguous range, not an extra sentence cut inside it.
        if (v && rec.spanVariants?.some(span=>span.id===v.id) && mine.length) {valid=false;break;}
        if (mine.some(t=>t.fromSec<b.srcStart||t.toSec>b.srcEnd)) {valid=false;break;}
        const cut=mine.reduce((n,t)=>n+t.toSec-t.fromSec,0), duration=b.srcEnd-b.srcStart-cut;
        if (duration<=0) {valid=false;break;}
        spokenSec+=duration;trimmedSec+=cut;
      }
      if (valid) options.push({group,bounds,spokenSec,trimmedSec,variant:v?{id:v.id,decisionId:v.review.decisionId}:undefined});
    }
    // A wholly wordless optional component is simply silence; do not duplicate recipes.
    return options;
  });
  const out: Recipe[] = [];
  function visit(i:number, passages:Passage[]) {
    if(i<choices.length){for(const p of choices[i])visit(i+1,p?[...passages,p]:passages);return;}
    if(!passages.length)return;
    const kept=passages.flatMap(p=>p.bounds.map(b=>b.island));
    const gaps=effectiveGaps(rec,kept), internal=kept.slice(0,-1).map((left,i)=>passages.some(p=>p.bounds.some(b=>b.island===left)&&p.bounds.some(b=>b.island===kept[i+1])));
    const spokenSec=passages.reduce((n,p)=>n+p.spokenSec,0);
    out.push({passages,kept,gaps,internal,spokenSec,
      floor:spokenSec+gaps.reduce((n,g)=>n+g.minSec,0),ceiling:spokenSec+gaps.reduce((n,g)=>n+g.maxSec,0),trims});
  }
  visit(0,[]);
  if (cache.cueRecipes.size >= 8) cache.cueRecipes.delete(cache.cueRecipes.keys().next().value!);
  cache.cueRecipes.set(cueKey,out);return out;
}
/** Exact-target intervals, split at every cue-policy boundary. Preset slack is queried separately. */
function targetIntervals(rec:Recording,cadence:Cadence):TargetInterval[] {
  const cache=cacheFor(rec),cached=cache.intervals.get(cadence);
  if(cached)return cached;
  const endpoints=new Set<number>([0]);
  for(const cue of rec.durationCues){
    if(cue.precision==='vague')continue;
    const review=cueReviewFor(rec,cue);if(review.action==='ignore')continue;
    for(const n of review.band??[cue.bandMinSec,cue.bandMaxSec])if(Number.isFinite(n)&&n>=0)endpoints.add(n);
  }
  const points=[...endpoints].sort((a,b)=>a-b);
  const regions:Array<TargetInterval&{sample:number}>=[];
  for(let i=0;i<points.length;i++){
    const minSec=points[i],maxSec=points[i+1]??Infinity;
    if(minSec>0)regions.push({minSec,maxSec:minSec,sample:minSec,minInclusive:true,maxInclusive:true});
    if(maxSec>minSec)regions.push({minSec,maxSec,sample:Number.isFinite(maxSec)?(minSec+maxSec)/2:minSec+1,
      minInclusive:false,maxInclusive:false});
  }
  const [low,high]=compositionPolicy(cadence,1).band,out:TargetInterval[]=[];
  for(const region of regions){
    let candidates:Recipe[];
    try { candidates=recipes(rec,region.sample); } catch(error) {
      if(error instanceof InvalidEditError)continue;
      throw error;
    }
    for(const r of candidates){
    const min=Math.max(r.floor,r.spokenSec/high),max=Math.min(r.ceiling,r.spokenSec/low);
    const minSec=Math.max(min,region.minSec),maxSec=Math.min(max,region.maxSec);
    const minInclusive=minSec!==region.minSec||region.minInclusive!==false;
    const maxInclusive=maxSec!==region.maxSec||region.maxInclusive!==false;
    if(minSec<maxSec||(minSec===maxSec&&minInclusive&&maxInclusive))out.push({minSec,maxSec,minInclusive,maxInclusive});
    }
  }
  const merged:TargetInterval[]=[];
  for(const interval of out.sort((a,b)=>a.minSec-b.minSec||Number(b.minInclusive)-Number(a.minInclusive)||a.maxSec-b.maxSec)){
    const last=merged.at(-1);
    if(last&&(interval.minSec<last.maxSec||(interval.minSec===last.maxSec&&(last.maxInclusive||interval.minInclusive)))){
      if(interval.maxSec>last.maxSec){last.maxSec=interval.maxSec;last.maxInclusive=interval.maxInclusive;}
      else if(interval.maxSec===last.maxSec)last.maxInclusive=last.maxInclusive||interval.maxInclusive;
    }else merged.push({...interval});
  }
  cache.intervals.set(cadence,merged);return merged;
}
/** Projection onto bounded gaps with exact total; practice initially shares evenly. */
function allocate(recipe:Recipe,budget:number):number[]{
  const {gaps,internal}=recipe;
  if(!gaps.length)return [];
  const pref=gaps.map((g,i)=>internal[i]?Math.max(g.minSec,Math.min(g.maxSec,g.nativeSec)):0);
  const project=(wanted:number[])=>{
    let lo=Math.min(...gaps.map((g,i)=>g.minSec-wanted[i]));
    let hi=Math.max(...gaps.map((g,i)=>g.maxSec-wanted[i]));
    for(let n=0;n<48;n++){
      const mid=(lo+hi)/2,total=gaps.reduce((sum,g,i)=>sum+Math.max(g.minSec,Math.min(g.maxSec,wanted[i]+mid)),0);
      if(total<budget)lo=mid;else hi=mid;
    }
    const lengths=gaps.map((g,i)=>Math.max(g.minSec,Math.min(g.maxSec,wanted[i]+(lo+hi)/2)));
    let residue=budget-lengths.reduce((n,v)=>n+v,0);
    for(let i=0;i<lengths.length&&residue!==0;i++){
      const delta=Math.max(gaps[i].minSec-lengths[i],Math.min(gaps[i].maxSec-lengths[i],residue));
      lengths[i]+=delta;residue-=delta;
    }return lengths;
  };
  // Internal pauses get their recorded rhythm first. Practice consumes the rest within bounds.
  const practice=gaps.map((g,i)=>internal[i]?{...g,minSec:pref[i],maxSec:pref[i]}:g);
  const floor=practice.reduce((n,g)=>n+g.minSec,0),ceiling=practice.reduce((n,g)=>n+g.maxSec,0);
  if(budget>=floor&&budget<=ceiling){
    let lo=0,hi=budget;
    for(let n=0;n<48;n++){
      const mid=(lo+hi)/2,total=practice.reduce((sum,g,i)=>sum+(internal[i]?pref[i]:Math.max(g.minSec,Math.min(g.maxSec,mid))),0);
      if(total<budget)lo=mid;else hi=mid;
    }
    return project(practice.map((g,i)=>internal[i]?pref[i]:Math.max(g.minSec,Math.min(g.maxSec,(lo+hi)/2))));
  }
  return project(pref);
}
function build(recipe:Recipe,rec:Recording,targetSec:number,actualSec:number,cadence:Cadence,opts:PlanOptions,candidateCount:number):SessionPlan{
  const lengths=allocate(recipe,actualSec-recipe.spokenSec),timeline:Timed[]=[];
  const sections:CompositionProof['sections']=[];
  let t=0,gi=0;
  for(const passage of recipe.passages){
    const startSec=t;
    for(const b of passage.bounds){
      const full=rec.speech[b.island];
      const retained=b.srcStart===full.srcStart&&b.srcEnd===full.srcEnd?undefined:b;
      const s=speechSegments(rec,b.island,t,recipe.trims,retained);timeline.push(...s.segs);t=s.end;
      if(gi<lengths.length){
        const length=lengths[gi];if(length>0)timeline.push({kind:'tone',startSec:t,endSec:t+length,gap:gi});
        t+=length;gi++;
      }
    }
    // The connector to the next section is outside this section's envelope.
    const last=passage.bounds.at(-1)!.island;
    const speechEnd=[...timeline].reverse().find(s=>s.kind==='speech'&&s.island===last)!.endSec;
    sections.push({role:passage.group.role,islands:passage.bounds.map(b=>b.island),spokenSec:passage.spokenSec,startSec,endSec:speechEnd});
  }
  const policy=compositionPolicy(cadence,t),[low,high]=policy.band;
  const outside=opts.desiredSpeechRatio!==undefined&&(opts.desiredSpeechRatio<low||opts.desiredSpeechRatio>high);
  const preferred=outside?policy.preferred:opts.desiredSpeechRatio??policy.preferred;
  const middle=sections.filter(s=>s.role==='middle'),meaningful=middle.filter(s=>s.spokenSec>=30);
  const countDistance=Math.abs(meaningful.length-policy.middleCount);
  const normalizedLengthDistance=sections.reduce((n,s)=>n+Math.abs(s.spokenSec/t-policy[s.role]),0);
  const first=sections[0],last=sections.at(-1)!,available=Math.max(0,last.startSec-first.endSec);
  const spacingCost=middle.length?middle.reduce((n,s,i)=>n+(available>0?(((s.startSec+s.endSec)/2-(first.endSec+available*(i+1)/(middle.length+1)))/available)**2:1),0)/middle.length:0;
  const internals=recipe.gaps.flatMap((g,i)=>recipe.internal[i]&&g.nativeSec>0?[(lengths[i]/g.nativeSec-1)**2]:[]);
  const rhythmCost=internals.length?internals.reduce((n,v)=>n+v,0)/internals.length:0;
  const deficits:string[]=[];
  if(meaningful.length<policy.middleRange[0]||meaningful.length>policy.middleRange[1])deficits.push('middle-count');
  if(middle.some(s=>s.spokenSec<30))deficits.push('tiny-middle');
  for(const [role,cap] of [['opening',policy.caps[0]],['closing',policy.caps[1]],['middle',policy.caps[2]]] as const){
    if(sections.some(s=>s.role===role&&s.spokenSec>cap*t))deficits.push(`${role}-length`);
  }
  if(cadence==='frequent'&&!middle.some(s=>s.spokenSec>=.04*t))deficits.push('longer-middle');
  const variants=recipe.passages.flatMap(p=>p.variant?[p.variant]:[]);
  const shape={score:10*Math.abs(recipe.spokenSec/t-preferred)+countDistance+normalizedLengthDistance+spacingCost+rhythmCost,
    deficits,preferredMiddleCount:policy.middleCount,middleRange:policy.middleRange,middleCount:meaningful.length,
    countDistance,normalizedLengthDistance,spacingCost,rhythmCost};
  return {recordingId:rec.id,targetSec,actualSec:t,cadence,timeline,
    preview:{spokenSec:recipe.spokenSec,reminderCount:meaningful.length,
      longestSilenceSec:Math.max(0,...timeline.filter(s=>s.kind==='tone').map(s=>s.endSec-s.startSec)),
      cueTrimmed:recipe.passages.some(p=>p.trimmedSec>0)},
    composition:{policyVersion:COMPOSITION_POLICY_VERSION,band:[low,high],preferredShare:preferred,preferenceOutsideBand:outside,
      variants,wordlessIslands:[...(rec.wordlessIslands??[])].sort((a,b)=>a-b),sections,shape,candidateCount,
      keptIslands:recipe.kept,gaps:recipe.gaps,comingOutIsland:comingOutIsland(rec)}};
}
function compare(a:SessionPlan,b:SessionPlan):number{
  const pa=a.composition!,pb=b.composition!,score=pa.shape.score-pb.shape.score;
  if(Math.abs(score)>1e-12)return score;
  const miss=Math.abs(a.actualSec-a.targetSec)-Math.abs(b.actualSec-b.targetSec);if(Math.abs(miss)>1e-12)return miss;
  if(pa.variants.length!==pb.variants.length)return pa.variants.length-pb.variants.length;
  if(a.preview.spokenSec!==b.preview.spokenSec)return b.preview.spokenSec-a.preview.spokenSec;
  for(let i=0;i<Math.max(pa.keptIslands.length,pb.keptIslands.length);i++){
    const d=(pa.keptIslands[i]??-1)-(pb.keptIslands[i]??-1);if(d)return d;
  }
  const va=pa.variants.map(v=>v.id).join('\0'),vb=pb.variants.map(v=>v.id).join('\0');
  if(va!==vb){const aa=Array.from(va),bb=Array.from(vb);for(let i=0;i<Math.max(aa.length,bb.length);i++){const d=(aa[i]?.codePointAt(0)??-1)-(bb[i]?.codePointAt(0)??-1);if(d)return d;}}
  for(let i=0;i<Math.min(a.timeline.length,b.timeline.length);i++){
    const d=Math.round((a.timeline[i].endSec-a.timeline[i].startSec)*48000)-Math.round((b.timeline[i].endSec-b.timeline[i].startSec)*48000);if(d)return d;
  }return 0;
}
export function composeSession(rec:Recording,targetSec:number,cadence:Cadence,opts:PlanOptions&{includeWithheld?:boolean}={}):CompositionResult{
  if(!opts.includeWithheld&&isWithheld(rec.id))return fail('withheld','This recording is not released for sitting.');
  const tolerance=opts.toleranceSec??30;
  if(!Number.isFinite(targetSec)||targetSec<=0||!Number.isFinite(tolerance)||tolerance<0
      || (opts.desiredSpeechRatio!==undefined&&(!Number.isFinite(opts.desiredSpeechRatio)||opts.desiredSpeechRatio<=0||opts.desiredSpeechRatio>1))) {
    return fail('invalid-review','Invalid target, tolerance or speech preference.');
  }
  const cache=cacheFor(rec),key=`${COMPOSITION_POLICY_VERSION}/${targetSec}/${cadence}/${tolerance}/${opts.desiredSpeechRatio??''}`;
  const cached=cache.results.get(key);if(cached)return cached;
  let result:CompositionResult;
  try{
    const candidates=recipes(rec,targetSec),policy=compositionPolicy(cadence,targetSec),[low,high]=policy.band;
    const feasibleTargets=targetIntervals(rec,cadence);
    const durationPossible=candidates.some(r=>r.floor<=targetSec+tolerance&&r.ceiling>=targetSec-tolerance);
    let best:SessionPlan|undefined;
    for(const r of candidates){
      const min=Math.max(r.floor,r.spokenSec/high),max=Math.min(r.ceiling,r.spokenSec/low);
      if(min>max||min>targetSec+tolerance||max<targetSec-tolerance)continue;
      const actual=Math.max(min,Math.min(max,targetSec));
      const p=build(r,rec,targetSec,actual,cadence,opts,candidates.length);
      // Floating sums do not widen the band. Correct a boundary by moving only silence
      // an ulp inward, then recheck the actual timeline denominator.
      const share=p.preview.spokenSec/p.actualSec;
      if(share<low||share>high){
        const inward=actual+(share>high?1:-1)*1e-9;
        if(inward<r.floor||inward>r.ceiling||Math.abs(inward-targetSec)>tolerance)continue;
        const corrected=build(r,rec,targetSec,inward,cadence,opts,candidates.length);
        if(corrected.preview.spokenSec/corrected.actualSec<low||corrected.preview.spokenSec/corrected.actualSec>high)continue;
        if(!best||compare(corrected,best)<0)best=corrected;
      }else if(!best||compare(p,best)<0)best=p;
    }
    if(best){
      const diagnostics=planDiagnostics(best,rec);
      result=diagnostics.violations.length?fail('invalid-review',diagnostics.violations.join('; '),feasibleTargets,durationPossible)
        :{status:'conforming',plan:best,proof:best.composition!,feasibleTargets,durationPossible:true};
    }else result=fail(!candidates.length?(retainedCueConflicts(rec,targetSec).length?'cue-conflict':'no-safe-ending'):durationPossible?'speech-band':'duration',
      !candidates.length?'No complete, compatible speech ranges at this duration.':durationPossible?'No safe composition is in the requested speech band.':'No safe composition reaches this duration.',feasibleTargets,durationPossible);
  }catch(error){if(!(error instanceof InvalidEditError))throw error;result=fail('invalid-review',error.message);}
  if(cache.results.size>=32)cache.results.delete(cache.results.keys().next().value!);
  cache.results.set(key,result);return result;
}
